import {
  IonAvatar,
  IonButton,
  IonCheckbox,
  IonHeader,
  IonIcon,
  IonImg,
  IonModal,
  IonRow,
  IonSpinner,
  IonTextarea,
} from "@ionic/react";
import DamageDefault from "../assets/image/damageDefault.png";
import * as d3 from "d3";
import { arrowForward, checkmarkCircleOutline, close } from "ionicons/icons";
import ZoomSvg from "../../assets/svg/zoom.svg";
import TractorLeftSvg from "../../assets/svg/tractorLeft.svg";
import TractorRightSvg from "../../assets/svg/tractorRight.svg";
import TractorFrontSvg from "../../assets/svg/tractorFront.svg";
import { ReactSVG } from "react-svg";

import "./ReportDamageModal.css";
import { useEffect, useRef, useState } from "react";
import { svgMapping, tractorMapping } from "../../data/vehicleMapping";
import { Camera, CameraResultType, CameraSource } from "@capacitor/camera";
import { VehiclePart } from "../../types/api/vehiclePart.types";
import { useGetDamageTypeQuery } from "../../api/damage";
interface Step3Props {
  side: string;
  type: string;
  setStep: (step: number) => void;
  damageList: { [index: string]: boolean };
  selectedElement: string;
  description: string;
  setDescription: (des: string) => void;
  imageUrl: string;
  setImageUrl: (image: string) => void;
  vehicleParts: VehiclePart[];
  setCameraIsActive: (element: boolean) => void;
  storeDamage: () => void;
  setSelectedElement: (element: string) => void;
  setDamageList: (list: { [index: string]: boolean }) => void;
  setOpen: (isOpen: boolean) => void;
}

const Step3: React.FC<Step3Props> = ({
  side,
  type,
  setStep,
  selectedElement,
  damageList,
  description,
  setDescription,
  imageUrl,
  setImageUrl,
  vehicleParts,
  setCameraIsActive,
  storeDamage,
  setOpen,
  setDamageList,
  setSelectedElement,
}) => {
  const [finishModalIsOpen, setFinishModalIsOpen] = useState(false);
  const [currentSvg, setCurrentSvg] = useState("");
  const [storeLoading, setStoreLoading] = useState(false);

  useEffect(() => {
    setCurrentSvg(svgMapping(side, type));
  }, [side, type]);

  let vehiclePart = vehicleParts.filter(
    (el) => el.code === selectedElement
  )?.[0];

  const takePicture = async () => {
    setCameraIsActive(true);
    const image = await Camera.getPhoto({
      quality: 90,
      allowEditing: true,
      resultType: CameraResultType.DataUrl,
      correctOrientation: true,
      source: CameraSource.Camera,
    });
    setCameraIsActive(false);
    setImageUrl(image.dataUrl!);
    setStep(3);
  };

  useEffect(() => {
    if (finishModalIsOpen) {
      setTimeout(() => {
        setFinishModalIsOpen(false);
        setImageUrl("");

        setDamageList({});
        setDescription("");
        setSelectedElement("");
        setOpen(false);
        setStep(side === "Inside" || side === "TrailerInside" ? 1 : 0);
      }, 2000);
    }
  }, [finishModalIsOpen]);

  return (
    <>
      <IonModal
        isOpen={finishModalIsOpen}
        initialBreakpoint={1}
        breakpoints={[0, 1]}
      >
        {" "}
        <div className="ReportDamageModal-step4-end">
          <IonIcon
            src={checkmarkCircleOutline}
            className="ReportDamageModal-step4-end-icon"
          />
          <div className="ReportDamageModal-step4-end-text">
            Merci !<br />
            Le dommage a bien été ajouté
          </div>
        </div>
      </IonModal>
      <IonRow className="ion-justify-content-center">
        <div className="ReportDamageModal-zoomedcontainer">
          {currentSvg ? (
            <ReactSVG
              className="ReportDamageModal-tractor_zoomed"
              src={currentSvg}
              beforeInjection={(svg) => {
                svg.style.width = "100%";
                svg.style.height = "100%";
                svg.style.maxHeight = "180px";
                svg.setAttribute("preserveAspectRatio", "xMidYMid meet");
                svg.removeAttribute("width");
                svg.removeAttribute("height");
                if (!svg.hasAttribute("viewBox")) {
                  svg.setAttribute("viewBox", "0 0 500 500"); // à adapter
                }
              }}
              afterInjection={(svg) => {
                const svgElement = svg as SVGSVGElement;

                if (side === "Inside" || side === "TrailerInside") {
                  let zoom = d3.zoom().on("zoom", (e) => {
                    d3.select(svgElement)
                      .select("g")
                      .attr("transform", e.transform);
                  });
                  d3.select(svgElement).call(zoom as any);
                } else {
                  d3.select(svgElement).selectAll("g").attr("transform", null);

                  try {
                    const bbox = svgElement.getBBox();
                    if (selectedElement) {
                      const selectedPaths = d3
                        .select(svgElement)
                        .selectAll("path")
                        .filter(function () {
                          const id = (this as any)?.id?.split("-")[0];
                          return id === selectedElement;
                        });

                      if (selectedPaths.size() > 0) {
                        d3.select(svgElement)
                          .selectAll("path")
                          .style("opacity", "0.3");
                        selectedPaths.style("opacity", "1");

                        const pathBBox = (
                          selectedPaths.node() as any
                        )?.getBBox();

                        const topPadding = pathBBox.height * 0.25;
                        const bottomPadding = pathBBox.height * 0.5;
                        const sidePadding = pathBBox.width * 0.3;

                        const elementViewBox = `
                       ${pathBBox.x - sidePadding}
                       ${pathBBox.y - topPadding}
                       ${pathBBox.width + sidePadding * 2}
                       ${pathBBox.height + topPadding + bottomPadding}
                     `;
                        svgElement.setAttribute(
                          "viewBox",
                          elementViewBox.replace(/\s+/g, " ").trim()
                        );
                      }
                    } else {
                      const padding = Math.max(bbox.width, bbox.height) * 0.15;
                      const viewBox = `${bbox.x - padding} ${
                        bbox.y - padding
                      } ${bbox.width + padding * 2} ${
                        bbox.height + padding * 2
                      }`;
                      svgElement.setAttribute("viewBox", viewBox);
                    }
                  } catch (e) {
                    console.error("Error setting viewBox:", e);
                  }
                  const zoom = d3
                    .zoom()
                    .scaleExtent([0.1, 10])
                    .on("zoom", (event) => {
                      d3.select(svgElement)
                        .select("g")
                        .attr("transform", event.transform);
                    });

                  d3.select(svgElement).call(zoom as any);
                }
              }}
            />
          ) : null}
        </div>
      </IonRow>

      <div className="ReportDamageModal-footer">
        <IonRow className="ReportDamageModal-step3-title">
          {selectedElement ? vehiclePart?.title : ""}
        </IonRow>
        <IonRow className="ReportDamageModal-step3-subtitle ">
          Apportez un commentaire
        </IonRow>
        <div>
          <IonTextarea
            placeholder="Apportez quelques précisions sur le dommage (ex : la rayure mesure 30cm et se trouve en bas à droite... )"
            className="ReportDamageModal-textarea"
            rows={5}
            value={description}
            onIonInput={(e) => {
              setDescription(e.target.value as string);
            }}
          ></IonTextarea>
        </div>
        {Object.values(damageList).some((value) => value) ? (
          <IonButton
            color={"success"}
            expand="block"
            className="ReportDamageModal-button_success"
            onClick={async () => takePicture()}
          >
            Prendre en photo le {side === "Document" ? "défaut" : "dommage"}
            <IonIcon icon={arrowForward} slot="end" />
          </IonButton>
        ) : null}
        {Object.values(damageList).some((value) => value) &&
        side === "Document" ? (
          <IonButton
            color={"success"}
            expand="block"
            onClick={async () => {
              setStoreLoading(true);
              await storeDamage();
              setStoreLoading(false);
              setFinishModalIsOpen(true);
            }}
            className="ReportDamageModal-button_success"
          >
            Valider le nouveau défaut
            {storeLoading ? (
              <IonSpinner
                name="circular"
                slot="end"
                class="ReportDamageModal-loader"
              ></IonSpinner>
            ) : (
              <IonIcon icon={arrowForward} slot="end" />
            )}
          </IonButton>
        ) : null}
      </div>
    </>
  );
};

export default Step3;
